import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  Alert,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../contexts/AuthContext';
import GoogleIcon from '@mui/icons-material/Google';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import {useGoogleLogin} from "@react-oauth/google";

const schema = yup.object({
  username: yup.string().min(3, 'Username deve essere di almeno 3 caratteri').required('Username richiesto'),
  email: yup.string().email('Email non valida').required('Email richiesta'),
  password: yup.string().min(6, 'Password deve essere di almeno 6 caratteri').required('Password richiesta'),
  confirmPassword: yup.string().oneOf([yup.ref('password')], 'Le password non coincidono').required('Conferma password richiesta'),
});

type FormData = yup.InferType<typeof schema>;

const RegisterPage: React.FC = () => {
  const { register: registerUser, googleLogin, linkedinLogin } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const handleGoogleSuccess = async (tokenResponse: any) => {
    console.log('✅ Token ricevuto, recupero info utente...');
    try {
      setIsLoading(true);
      setError('');
      const userInfoRes = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
        headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
      });
      const userInfo = await userInfoRes.json();
      await googleLogin({ userInfo });
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore durante il login');
    } finally {
      setIsLoading(false);
    }
  };

  const googleLoginHook = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: () => {
      console.error('Login fallito');
      setError('Login con Google fallito');
    },
    flow: 'implicit',
  });

  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      setError('');
      await registerUser(data.email, data.password, data.username);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Errore durante la registrazione');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    //googleLogin();
  };

  const handleLinkedInLogin = () => {
    linkedinLogin();
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            Registrati su CV Portal
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              autoComplete="username"
              autoFocus
              {...register('username')}
              error={!!errors.username}
              helperText={errors.username?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              autoComplete="email"
              {...register('email')}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              {...register('password')}
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Conferma Password"
              type="password"
              id="confirmPassword"
              autoComplete="new-password"
              {...register('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
            >
              {isLoading ? <CircularProgress size={24} /> : 'Registrati'}
            </Button>
            
            <Divider sx={{ my: 2 }}>
              <Typography variant="body2" color="text.secondary">
                oppure
              </Typography>
            </Divider>

            <Button
              fullWidth
              variant="contained"
              onClick={() => googleLoginHook()}
              startIcon={<GoogleIcon/>}
              sx={{
                py: 1.5,
                bgcolor: '#4285f4',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#3367d6',
                },
                fontSize: '1rem',
                fontWeight: 500,
                textTransform: 'none',
              }}
            >
              Registrati con Google
            </Button>
            
            {/*<Button
              fullWidth
              variant="outlined"
              onClick={handleLinkedInLogin}
              startIcon={<LinkedInIcon />}
              sx={{ mb: 2 }}
            >
              Registrati con LinkedIn
            </Button>*/}
            
            <Box textAlign="center">
              <Typography variant="body2">
                Hai già un account?{' '}
                <MuiLink component={Link} to="/login">
                  Accedi
                </MuiLink>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterPage;
